import Link from 'next/link';
import React, { useCallback, useMemo } from 'react';
import formatMomentDate from '../../../../../utils/formatMomentDate';
import toMskMoment from '../../../../../utils/toMskMoment';
import { MskIcon } from '../../../../Icons';
import styles from './MainPurchaseTab.module.scss';
import { MainPurchaseTabProps } from './MainPurchaseTab.type';
import { RestrictionAlert } from '../../../../_shared/Alerts/RestrictionAlert';
import DescriptionList from '../../../components/DescriptionList';
import DescriptionListItem from '../../../components/DescriptionListItem';

const excludedProps = ['finish_ts', 'start_ts', 'receive_place', 'receive'];
const alwaysVisibleProps = ['allow_multiple_offer'];

const MainPurchaseTab: React.FC<MainPurchaseTabProps> = ({
    purchase,
    canPreviewAnnounce,
}) => {
    const receivePlace = useMemo(() => {
        return purchase?.phases
            ?.find((phase: any) =>
                ['receive', 'rt_receive']?.includes(phase?.block?.name)
            )
            ?.props?.find((prop: any) => prop?.name === 'receive_place')?.value;
    }, [purchase]);

    const purchasePhase = useMemo(() => {
        const isCanceledPurchase = purchase.status.name === 'canceled';
        return isCanceledPurchase
            ? 'Закупка отменена'
            : purchase?.phases?.find(
                  (phase: any) => phase?.id === purchase?.phaseId
              )?.block?.title;
    }, [purchase?.phaseId, purchase?.phases, purchase.status.name]);

    const getPhaseClass = useCallback(
        (phaseId: number) => {
            if (phaseId === purchase?.phaseId) {
                return styles.green;
            }
            if (phaseId < purchase?.phaseId) {
                return styles.lightGrey;
            }
            return '';
        },
        [purchase]
    );

    const additionalInfoProps = useMemo(() => {
        return purchase.phases
            .flatMap((phase) => phase.props)
            .filter(
                (prop) =>
                    !excludedProps.includes(prop.name) &&
                    prop.type === 'boolean' &&
                    (prop.isPublic || alwaysVisibleProps.includes(prop.name))
            );
    }, [purchase]);

    const getPhaseDates = useCallback(
        (phase: any): string => {
            const startValue = phase.props.find(
                (p: any) => p.name === 'start_ts'
            )?.value;
            const finishValue = phase.props.find(
                (p: any) => p.name === 'finish_ts'
            )?.value;

            const isReglamentAccreditation =
                purchase?.procedure?.reglament?.name === 'accreditation';

            if (isReglamentAccreditation && !finishValue) {
                return 'Бессрочно';
            }

            return [
                startValue
                    ? formatMomentDate(toMskMoment(startValue), 'datetime')
                    : 'Не указано',
                ' - ',
                finishValue
                    ? formatMomentDate(toMskMoment(finishValue), 'datetime')
                    : 'Не указано',
            ].join('');
        },
        [purchase?.procedure?.reglament?.name]
    );

    return (
        <div className={styles.container}>
            {canPreviewAnnounce && <RestrictionAlert lots={purchase.lots} />}
            <div className={styles.phases}>
                {purchase?.phases
                    ?.filter((phase: any) => {
                        return (
                            phase?.block?.name !== 'formation' &&
                            phase?.block?.name !== 'dealing' &&
                            phase?.block?.name !== 'paused'
                        );
                    })
                    ?.map((phase: any, idx: number) => {
                        const isCurrentPhase = phase?.id === purchase?.phaseId;
                        return (
                            <div
                                key={phase.id}
                                className={getPhaseClass(phase?.id)}
                            >
                                <div>{phase?.block?.title}</div>
                                <div>{getPhaseDates(phase)}</div>
                                {isCurrentPhase && (
                                    <div className={styles.currentPhase}>
                                        Текущий этап
                                    </div>
                                )}
                                {idx === 0 && <MskIcon />}
                            </div>
                        );
                    })}
            </div>

            <DescriptionList>
                <DescriptionListItem
                    title="Код закупки"
                    definition={purchase.number}
                />
                <DescriptionListItem
                    title="Наименование закупки"
                    definition={purchase.title}
                />
                <DescriptionListItem
                    title="Описание"
                    definition={purchase.description}
                />
                <DescriptionListItem
                    title="Организатор"
                    definition={
                        <Link
                            href={`/purchases?sectionOrg=${purchase.organization?.id}`}
                        >
                            <a>{purchase.organization?.name}</a>
                        </Link>
                    }
                />
                <DescriptionListItem
                    title="Способ закупки"
                    definition={purchase.procedure?.title}
                />
                <DescriptionListItem
                    title="Этап закупки"
                    definition={purchasePhase}
                />
                <DescriptionListItem
                    title="Закупка с ограниченным участием"
                    definition={purchase.procedure?.isPrivate ? 'Да' : 'Нет'}
                />

                {!!purchase.numberOos && (
                    <>
                        <DescriptionListItem
                            title="Номер извещения в ЕИС"
                            definition={purchase.numberOos}
                        />
                        <DescriptionListItem
                            title="Ссылка на извещение в ЕИС"
                            definition={
                                <a
                                    href={`https://zakupki.gov.ru/epz/order/extendedsearch/results.html?searchString=${purchase.numberOos}`}
                                    target="_blank"
                                    rel="nofollow noopener noreferrer"
                                >
                                    {purchase.numberOos}
                                </a>
                            }
                        />
                    </>
                )}

                <DescriptionListItem
                    title="Место приёма заявок"
                    definition={receivePlace}
                />
            </DescriptionList>

            {!!additionalInfoProps.length && (
                <>
                    <h3>Дополнительная информация</h3>
                    <DescriptionList>
                        {additionalInfoProps.map((prop) => (
                            <DescriptionListItem
                                key={prop.id}
                                title={prop.title}
                                definition={!!prop.value ? 'Да' : 'Нет'}
                            />
                        ))}
                    </DescriptionList>
                </>
            )}
        </div>
    );
};

export default React.memo(MainPurchaseTab);
